import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useToggle from '../hooks/useToggle';
import logout from '../services/auth/logout';
import { useCurrentUserStore } from '../store/RootStoreContext';
import { grispiNotification } from '../store/useNotificationStore';
import { getMaskedEmailForUI } from '../utils/helpers';
import { pushTo } from '../utils/routeHelper';
import ProfilePicture from './ProfilePicture';

const RightTopMenu = () => {
    const { email, fullName, profilePicture, id } = useCurrentUserStore();
    const {
        token: { margin }
    } = theme.useToken();

    const { t } = useTranslation('common');

    const [loading, setLoading] = useState(false);
    const [isDropdownOpen, toggle, , closeDropdown] = useToggle(false);

    const items: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'user-info',
                label: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ProfilePicture src={profilePicture} />
                        <div style={{ marginLeft: margin }}>
                            <div>{fullName}</div>
                            <div>{getMaskedEmailForUI(email)}</div>
                        </div>
                    </div>
                ),
                style: { cursor: 'default' }
            },
            { key: 'divider-1', type: 'divider' },
            {
                key: 'profile',
                label: t('dropdownMenu.editProfile'),
                icon: <UserOutlined />,
                onClick: () => {
                    closeDropdown();
                    pushTo(`/users/${id}/settings`);
                }
            },
            { key: 'divider-2', type: 'divider' },
            {
                key: 'logout',
                label: t('dropdownMenu.logout'),
                icon: <LogoutOutlined />,
                disabled: loading,
                onClick: async () => {
                    const startMessage = t('message.onLoggingOut');
                    const finishMessage = t('message.onLoggedOut') as string;

                    const key = 'logout-message';
                    try {
                        grispiNotification('loading', startMessage, key, 10);
                        setLoading(true);
                        closeDropdown();

                        await logout();

                        setLoading(false);

                        if (finishMessage) {
                            grispiNotification('success', finishMessage, key);
                            return;
                        }
                        grispiNotification('destroy', '', key);
                    } catch (error) {
                        grispiNotification('destroy', '', key);
                        setLoading(false);
                        grispiNotification('error', error?.response?.data?.message ?? error?.message, 'error');
                    }
                }
            }
        ],
        [profilePicture, margin, fullName, email, t, loading, closeDropdown, id]
    );

    return (
        <>
            <Dropdown
                menu={{
                    selectable: false,
                    openKeys: [],
                    items
                }}
                trigger={['click']}
                open={isDropdownOpen}
                onOpenChange={toggle}
                placement="bottomRight"
                arrow={{ pointAtCenter: true }}
            >
                <ProfilePicture src={profilePicture} style={{ cursor: 'pointer' }} />
            </Dropdown>
        </>
    );
};

export default observer(RightTopMenu);
